import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import {
  FacebookIcon,
  FacebookShareButton,
  XIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  TelegramIcon,
  TelegramShareButton,
  ThreadsIcon,
  ThreadsShareButton,
  LinkedinIcon,
  LinkedinShareButton,
} from 'react-share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import { useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CardMedia from '@mui/material/CardMedia';
import Grid from '@mui/material/Grid2';
import {
  useGetCulturalPosts,
  useGetCulturalPostsById,
} from '../../../hooks/hubHooks';
import { CulturalPost } from '../../../types';
import { formatDate } from '../../../utils';
import { AppConfig } from '../../../core/constants';
import ErrorDisplay from '../../../components/common/ErrorDisplay';

const averageWordsPerMinute = 200;

function calculateReadTime(text: string) {
  if (!text) return '0 min read';
  // Strip markdown formatting if needed (optional improvement)
  const plainText = text.replace(/[#_*>\-[\]()`~]/g, '');
  const wordCount = plainText.trim().split(/\s+/).length;
  const minutes = Math.ceil(wordCount / averageWordsPerMinute);
  return `${minutes} min read`;
}

function normaliseTags(tags: unknown): string[] {
  if (Array.isArray(tags)) {
    return tags.filter((tag): tag is string => typeof tag === 'string');
  }

  return typeof tags === 'string' ? [tags] : [];
}

function getRelatedPosts(
  currentPost: CulturalPost | undefined,
  posts: CulturalPost[],
) {
  const currentTags = new Set(
    normaliseTags(currentPost?.tags).map((tag) => tag.toLowerCase()),
  );

  if (!currentPost || currentTags.size === 0) {
    return [];
  }

  return posts
    .filter(
      (post) =>
        post.id !== currentPost.id &&
        normaliseTags(post.tags).some((tag) =>
          currentTags.has(tag.toLowerCase()),
        ),
    )
    .slice(0, 4);
}

export default function HubDetails() {
  const { contextId } = useParams();
  const navigate = useNavigate();
  const { typography } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');

  const [url, setUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setUrl(window.location.href);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const {
    data,
    isLoading,
    isError,
    error: postsError,
    refetch: postsRefetch,
  } = useGetCulturalPostsById(contextId ?? '');

  useEffect(() => {
    let postData = data as any;
    if (Array.isArray(postData?.tags) && postData.tags.length) {
      setSearchQuery(postData.tags[0]);
    }
  }, [data]);

  const {
    data: relatedData,
    isLoading: isRelatedDataLoading,
    isError: isRelatedDataError,
    error: errorQueryError,
    refetch: queryErrorRefetch,
  } = useGetCulturalPosts(searchQuery || '', 1, 5);

  if (isLoading || isRelatedDataLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
          mt: '64px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError || isRelatedDataError) {
    return (
      <ErrorDisplay
        error={postsError || errorQueryError}
        onRetry={postsRefetch || queryErrorRefetch}
      />
    );
  }

  const postDetails = data as CulturalPost | undefined;

  if (!postDetails) {
    return (
      <ErrorDisplay
        error={new Error('Cultural record was not found.')}
        onRetry={postsRefetch}
      />
    );
  }

  const readTime = calculateReadTime(postDetails.content);
  const relatedDataObject = relatedData as
    | { cultural_contexts?: CulturalPost[] }
    | undefined;
  const relatedPosts = getRelatedPosts(
    postDetails,
    relatedDataObject?.cultural_contexts || [],
  );

  const relatedTags = normaliseTags(postDetails.tags);

  return (
    <Box mt="64px">
      <Helmet>
        <title>
          {postDetails.title} | {AppConfig.appName}
        </title>
        <meta
          name="description"
          content={`View cultural post for ${postDetails.title}`}
        />
        <meta
          property="og:title"
          content={`${AppConfig.appName} | ${postDetails.title} - Cultural post`}
        />
        <meta
          property="og:description"
          content={`View cultural post for ${postDetails.title}`}
        />
      </Helmet>
      <Container
        maxWidth="xl"
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          my: { xs: 8, md: 12 },
          gap: { xs: 4, md: 6 },
          px: { xs: 2 },
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxWidth: 900,
            mx: 'auto',
          }}
        >
          <Typography
            sx={{
              mt: 2,
              lineHeight: 1.18,
              maxWidth: '100%',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              whiteSpace: 'normal',
              fontSize: {
                xs: typography.h4.fontSize,
                md: typography.h1.fontSize,
              },
              fontWeight: typography.h1.fontWeight,
              fontFamily:
                '"Playfair Display", "Georgia", "Times New Roman", serif !important',
            }}
          >
            {postDetails?.title}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              columnGap: 1.5,
              rowGap: 0.75,
              color: 'text.secondary',
            }}
          >
            <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
              Written by LegacyTies team
            </Typography>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: 'none', md: 'block' },
                height: 16,
                alignSelf: 'center',
              }}
            />

            <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
              {formatDate(postDetails?.updated_at)}
            </Typography>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: 'none', md: 'block' },
                height: 16,
                alignSelf: 'center',
              }}
            />

            <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
              {readTime}
            </Typography>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: 'none', md: 'block' },
                height: 16,
                alignSelf: 'center',
              }}
            />

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
                Share
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                <Button
                  size="small"
                  onClick={handleCopy}
                  aria-label="Copy link"
                  startIcon={
                    copied ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )
                  }
                  sx={{ minWidth: 0, px: 0.75 }}
                >
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={{ width: '100%', maxWidth: 1120, mx: 'auto' }}>
          {postDetails?.image_url && (
            <Box
              sx={{
                width: 'fit-content',
                maxWidth: '100%',
                mx: 'auto',
                borderRadius: 2,
                overflow: 'hidden',
                lineHeight: 0,
              }}
            >
              <CardMedia
                component="img"
                alt={postDetails?.title || 'Cultural record'}
                image={postDetails?.image_url}
                sx={{
                  objectFit: 'contain',
                  width: '100%',
                  maxHeight: { xs: 360, sm: 520, md: 640 },
                  display: 'block',
                  borderRadius: 2,
                }}
              />
            </Box>
          )}
          {postDetails?.video_url && (
            <Box>
              <iframe
                width="100%"
                height="315"
                src={postDetails?.video_url}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </Box>
          )}
          {postDetails?.audio_url && (
            <Box
              sx={{
                width: '100%',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                my: 2,
                boxShadow: 1,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.02)',
                },
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                🎧 Listen to the audio
              </Typography>
              <audio
                controls
                controlsList="nodownload"
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  marginTop: '12px',
                  outline: 'none',
                  backgroundColor: 'transparent',
                }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <source src={postDetails?.audio_url} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </Box>
          )}
        </Box>

        <Box sx={{ width: '100%', maxWidth: 780, mx: 'auto' }}>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
            {postDetails?.content}
          </ReactMarkdown>
        </Box>

        {postDetails?.link_url && (
          <Box textAlign="center">
            <Link
              href={postDetails?.link_url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {postDetails?.link_url}
            </Link>
          </Box>
        )}

        {(relatedTags.length > 0 || relatedPosts.length > 0) && (
          <>
            <Divider />
            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                mx: 0,
                bgcolor: 'transparent',
                p: 0,
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 3, md: 4 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 1.5,
                  maxWidth: 720,
                  mx: 'auto',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1.1,
                    fontSize: {
                      xs: typography.h4.fontSize,
                      md: typography.h3.fontSize,
                    },
                  }}
                >
                  Related cultural records
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    maxWidth: '46ch',
                  }}
                >
                  Continue discovering stories, traditions, and cultural
                  memories connected to this topic.
                </Typography>
              </Box>

              {relatedTags.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  {relatedTags.map((tag: string, index: number) => (
                    <Box
                      key={`${tag}-${index}`}
                      component="button"
                      type="button"
                      onClick={() =>
                        navigate('/hub', { state: { query: tag } })
                      }
                      sx={{
                        border: 'none',
                        borderRadius: 999,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        px: 1.75,
                        py: 0.9,
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textTransform: 'none',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      {tag}
                    </Box>
                  ))}
                </Box>
              )}

              {relatedPosts.length > 0 && (
                <Grid
                  container
                  spacing={3}
                  columns={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {relatedPosts.map((post, index) => {
                    const hasImage = Boolean(
                      post.image_url && post.image_url.trim(),
                    );

                    return (
                      <Grid
                        key={`${post.id}_${index}`}
                        size={{ xs: 12, sm: 6, md: 3 }}
                      >
                        <Box
                          component="article"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            cursor: 'pointer',
                            '&:hover img, &:hover .related-post-fallback': {
                              transform: 'scale(1.03)',
                            },
                          }}
                          onClick={() => {
                            navigate(`/hub/${post.id}`);
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: 4,
                              overflow: 'hidden',
                              height: 220,
                            }}
                          >
                            {hasImage ? (
                              <CardMedia
                                component="img"
                                image={post.image_url}
                                alt={post.title || 'Related cultural record'}
                                sx={{
                                  height: '100%',
                                  objectFit: 'cover',
                                  transition: 'transform 0.3s ease',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  height: '100%',
                                  bgcolor: 'action.hover',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '2.5rem',
                                  fontWeight: 700,
                                  color: 'text.primary',
                                  transition: 'transform 0.3s ease',
                                }}
                              >
                                {post.title?.charAt(0)?.toUpperCase() || 'R'}
                              </Box>
                            )}
                          </Box>

                          <Box
                            sx={{
                              pt: 2,
                              px: 0.5,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                              flexGrow: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 500,
                              }}
                            >
                              {formatDate(post?.updated_at)}
                            </Typography>

                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                lineHeight: 1.35,
                                display: '-webkit-box',
                                overflow: 'hidden',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 3,
                                color: 'text.primary',
                              }}
                            >
                              {post.title}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </>
        )}

        <Box
          sx={{ width: '100%', maxWidth: 780, mx: 'auto', textAlign: 'center' }}
        >
          <Typography variant="h6" gutterBottom>
            Pass this on
          </Typography>
          <Box
            display="flex"
            gap={1}
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
          >
            <FacebookShareButton url={url} hashtag={AppConfig.siteName}>
              <FacebookIcon size={32} round />
            </FacebookShareButton>

            <TwitterShareButton
              url={url}
              title={postDetails?.title}
              hashtags={[AppConfig.siteName]}
            >
              <XIcon size={32} round />
            </TwitterShareButton>

            <WhatsappShareButton url={url} title={postDetails?.title}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>

            <TelegramShareButton url={url} title={postDetails?.title}>
              <TelegramIcon size={32} round />
            </TelegramShareButton>

            <LinkedinShareButton url={url} title={postDetails?.title}>
              <LinkedinIcon size={32} round />
            </LinkedinShareButton>

            <ThreadsShareButton url={url} title={postDetails?.title}>
              <ThreadsIcon size={32} round />
            </ThreadsShareButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
